import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import {useForm} from 'react-hook-form';
import { reachGoal } from 'utils/metrica';

import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {Check as CheckIcon, WarningAmber} from '@vedmarket/ui-kit';

import {COLORS} from 'config/constants';
import {
  useAddServicesToRouteByOrderIdMutation,
  useSetCompletedByOrderIdMutation,
  useSummary,
} from 'api/calc';
import {useGetContainersQuery} from 'api/containers';
import {useGetCalcIdFromRoute} from 'utils/hooks';
import {useDemoPackagingAction} from 'components/cabinet/demo/steps/packaging/use-packaging';
import {checkDecimalOrZero} from 'utils/validation';
import {getContainerDataValue, getContainerFieldItems, METHODS} from '../../index';

import './edit_containers.scss';

const EditContainers = ({onClose, onSave}: PackagingEditContainersProps) => {
  const {t} = useTranslation();
  const orderId = useGetCalcIdFromRoute();
  const summary = useSummary();
  const {data: containers = []} = useGetContainersQuery();
  const [ setCompleted ] = useSetCompletedByOrderIdMutation();
  const [ addServices ] = useAddServicesToRouteByOrderIdMutation();

  const hasContainersManual = !isEmpty(summary?.containers_manual);
  const [ isManualMode, setIsManualMode ] = useState(hasContainersManual);
  const [ containersValueHasChanged, setContainersValueHasChanged ] = useState(false);
  const containersData = summary?.containers_user;
  const containerFields = useMemo(
    () => getContainerFieldItems(containers, containersData),
    [ containers, containersData ],
  );

  const {register, handleSubmit, getValues, reset, formState: {isValid, errors}} = useForm<ContainersManual>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  const demoPlayback = useDemoPackagingAction({
    action: 'confirm-delivery-method',
    disabled: isManualMode && !isValid,
    isAllowed: true,
  });

  useEffect(() => {
    const values = Object.fromEntries(containerFields.map(({code}) => [
      code,
      getContainerDataValue(containersData, code) || 0,
    ]));

    reset(values);
    setContainersValueHasChanged(false);
  }, [ containerFields, containersData, reset ]);

  useEffect(() => {
    setIsManualMode(hasContainersManual);
  }, [ hasContainersManual ]);

  const handleAutomaticChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const automatic = event.target.checked;
    setIsManualMode(!automatic);

    if (automatic) {
      const values = Object.fromEntries(containerFields.map(({code}) => [
        code,
        getContainerDataValue(summary?.containers, code) || 0,
      ]));

      reset(values);
      setContainersValueHasChanged(false);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    const shouldUseManualValues = isManualMode;
    const containersManual = shouldUseManualValues ? values : {};
    const saved = await onSave(containersManual);

    if (!saved) {
      return;
    }

    const deliveryMethodChanged = summary?.delivery_type === METHODS.COMBINED_CARGO;
    const containerModeChanged = hasContainersManual !== shouldUseManualValues;
    const shouldResetDelivery = deliveryMethodChanged || containerModeChanged || containersValueHasChanged;

    if (shouldResetDelivery) {
      await addServices({
        orderId,
        route: null,
        services: [],
      });

      await setCompleted({
        orderId,
        delivery: false,
        customs: false,
        additional_services: false,
        banking_commissions: false,
        certification: false,
      });
    }

    onClose && onClose();
  });

  return <form id='containers' className='packaging-edit-containers' onSubmit={onSubmit}>
    <div className='packaging-edit-containers__header'>
      <Typography
        variant='body2'
        sx={{
          fontWeight: 500,
          color: COLORS.BLACK_DARK,
          lineHeight: '21px',
        }}
      >{t('calc__text__packaging__calculatedNumberOfContainers')}</Typography>
      <FormControlLabel
        control={<Switch checked={!isManualMode} onChange={handleAutomaticChange} />}
        label={<Typography variant='body2'>{t('calc__text__packaging__valuesAreCalculatedAutomatically')}</Typography>}
        sx={{
          margin: 0,
          flexShrink: 0,
          minWidth: '238px',
        }}
      />
    </div>
    <div className='packaging-edit-containers__fields'>
      {containerFields.map(({code, label}) => <TextField
        key={code}
        {...register(code, {
          valueAsNumber: true,
          validate: (value?: number) => checkDecimalOrZero(value || 0, t('errors__message__onlyDecimals')),
          onChange: () => setContainersValueHasChanged(true),
        })}
        error={Boolean(errors[code])}
        helperText={errors[code]?.message}
        variant='outlined'
        label={<Typography component='span' variant='inherit'>{label}</Typography>}
        InputLabelProps={{shrink: true}}
        InputProps={{
          endAdornment: <InputAdornment position='end' sx={{padding: '0 2px'}}>
            <Typography variant='body1'>{t('units__text__items')}</Typography>
          </InputAdornment>,
        }}
        sx={{
          '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderStyle: 'dotted',
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
        }}
        disabled={!isManualMode}
      />)}
    </div>
    {summary?.not_enough_space_warning && <Alert
      severity='warning'
      variant='filled'
      icon={<WarningAmber style={{
        width: 22,
        height: 22,
      }} />}
      sx={{
        backgroundColor: COLORS.ORANGE,
        color: COLORS.WHITE,
        padding: '8px 16px',
        '& .MuiAlert-icon': {
          color: 'inherit',
          opacity: 1,
          padding: '8px 0',
          marginRight: '16px',
        },
        '& .MuiAlert-message': {
          padding: '8px 0',
        },
      }}
    >
      <Typography variant='subtitle1' sx={{marginBottom: '4px', lineHeight: '24px', letterSpacing: 0, color: 'inherit'}}>
        {t('common__message__warning')}
      </Typography>
      <Typography variant='body2' sx={{color: 'inherit', lineHeight: '21px', letterSpacing: 0}}>{t('calc__text__packaging__containersDoNotFitError')}</Typography>
    </Alert>}
    <Button
      {...demoPlayback.buttonProps}
      variant='contained'
      fullWidth
      type='submit'
      onClick={() => reachGoal({
        goal: 'podtverdit_sposob_dostavki',
        params: {
          containers: Object.fromEntries(containerFields.map(({code}) => [ code, getValues(code) ])),
          automatic: !isManualMode,
        },
      })}
      disabled={demoPlayback.disabled}
      startIcon={<CheckIcon />}
    >
      <Typography component='span' variant='button'>
        {t('actions__text__confirmDeliveryMethodContainers')}
      </Typography>
    </Button>
  </form>;
};

export default EditContainers;
